import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, inject, Output, signal } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { LenderCardComponent } from './lender-card.component';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { ContactFormComponent } from './contact-form.component';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { OfficesService } from '@/services/api/franchises.service';
import { LenderContactsService } from '@/services/api/lender-contacts.service';
import { LenderContact } from '@/models/bank-models.model';

@Component({
    selector: 'app-lender-contact-form',
    imports: [
      CommonModule,
      ToastModule,
      ConfirmDialogModule,
      LenderCardComponent,
      TableModule,
      ButtonModule,
      InputTextModule,
      FormsModule,
      SelectModule,
      ContactFormComponent,
      TagModule
    ],
    template: `
    <p-toast></p-toast>
<p-confirmDialog />
<app-lender-card [lender]="lenderContactsService.selectedLender()" class="w-full" [selectable]="false"></app-lender-card>
<p-table [value]="contacts" dataKey="contactId" editMode="row" [tableStyle]="{'min-width': '50rem'}" *ngIf="!lenderContactsService.contactEdit()">
    <ng-template #caption>
        <div class="flex items-center justify-between">
            <span class="text-xl font-bold">Lender Contacts</span>
            <button pButton pRipple label="Add Contact" class="p-button-rounded p-button-text p-button-success" (click)="onAddContact()"></button>
        </div>
    </ng-template>
    <ng-template pTemplate="header">
        <tr>
            <th style="width:15%">First Name</th>
            <th style="width:15%">Last Name</th>
            <th style="width:20%">Email</th>
            <th style="width:15%">Phone</th>
            <th style="width:20%">Office</th>
            <th style="width:15%"></th>
        </tr>
    </ng-template>
    <ng-template pTemplate="body" let-contact let-editing="editing" let-ri="rowIndex">
        <tr [pEditableRow]="contact">
            <td>
                <p-cellEditor [style]="{'width':'15%'}">
                    <ng-template pTemplate="input">
                        <input
                            pInputText 
                            type="text" 
                            [(ngModel)]="contact.contactFirstName" />
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{contact.contactFirstName}}
                    </ng-template>
                </p-cellEditor>
            </td>
            <td>
                <p-cellEditor [style]="{'width':'15%'}">
                    <ng-template pTemplate="input">
                        <input 
                            pInputText type="text" 
                            [(ngModel)]="contact.contactLastName" 
                            required />
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{contact.contactLastName}}
                    </ng-template>
                </p-cellEditor>
            </td>
            <td>
                <p-cellEditor [style]="{'width':'15%'}">
                    <ng-template pTemplate="input">
                        <input 
                            pInputText 
                            type="text" 
                            [(ngModel)]="contact.contactEmail" />
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{contact.contactEmail}}
                    </ng-template>
                </p-cellEditor>
            </td>
            <td>
                <p-cellEditor [style]="{'width':'20%'}">
                    <ng-template pTemplate="input">
                        <input 
                            pInputText 
                            type="text" 
                            [(ngModel)]="contact.contactPhone" />
                    </ng-template>
                    <ng-template pTemplate="output">
                        {{contact.contactPhone}}
                    </ng-template>
                </p-cellEditor>
            </td>
            <td>
                <p-cellEditor [style]="{'width':'15%'}">
                    <ng-template pTemplate="input">
                        <p-select [options]="filteredOffices" optionValue="FID" optionLabel="CityCode" [ngModel]="contact.FID"
                        [style]="{'width':'100%'}"
                        placeholder="Select an office" />
                    </ng-template>
                    <ng-template pTemplate="output">
                        <p-tag 
                            [value]="getOffice(contact.FID)?.CityCode"  />
                    </ng-template>
                </p-cellEditor>
            </td>
            <td >
                <div class="flex align-items-center justify-content-center gap-2">
                    <button 
                        pButton 
                        pRipple 
                        type="button" 
                        icon="pi pi-pencil" 
                        (click)="onSelectContact(contact)" 
                        class="p-button-rounded p-button-text">
                    </button>
                    <button 
                        pButton 
                        pRipple 
                        type="button" 
                        icon="pi pi-trash" 
                        severity="danger"
                        (click)="confirmDeleteContact($event,contact)" 
                        class="p-button-rounded p-button-text">
                    </button>
                </div>
            </td>
        </tr>
    </ng-template>
</p-table>
<app-contact-form *ngIf="lenderContactsService.contactEdit()"></app-contact-form>
    `
})
export class LenderContactFormComponent {
    officeService = inject(OfficesService);
    lenderContactsService = inject(LenderContactsService); 
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
  contacts :LenderContact[] = [];
  contactEdit: boolean = false;
  clonedContacts: { [s: string]: LenderContact; } = {};

  constructor(
  ) {
    effect(() => {
      const contacts = this.lenderContactsService.selectedLender()?.LenderContacts || [];
      if (contacts.length > 0) {
        this.contacts = contacts.filter(c => c.FID in this.filteredOffices.map(o => o.FID));
      }
    });
  }

  get filteredOffices() {
    return this.officeService.getFilteredOffices('LenderContacts', 'edit');
  }

  getOffice(fid: number) {
    return this.officeService.getFilteredOffices('LenderContacts', 'edit').find(o => o.FID === fid);
  }

  onRowEditInit(contact: LenderContact) {
    this.clonedContacts[contact.contactId as unknown as string] = { ...contact };
}

onRowEditSave(contact: LenderContact) {
    if (contact.contactFirstName && contact.contactLastName) {
        delete this.clonedContacts[contact.contactId as unknown as string];
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Product is updated' });
    } else {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Invalid Price' });
    }
}

onAddContact() {
  this.lenderContactsService.setContactEdit(true);
  this.lenderContactsService.setSelectedContact(new LenderContact(this.lenderContactsService.selectedLender()?.LenderID));
}

onRowEditCancel(contact: LenderContact, index: number) {
    this.contacts[index] = this.clonedContacts[contact.contactId as unknown as string];
    delete this.clonedContacts[contact.contactId as unknown as string];
}

onSelectContact(contact: LenderContact) {
  console.log(contact);
  this.lenderContactsService.setSelectedContact(contact);
  this.lenderContactsService.setContactEdit(true);
}

confirmDeleteContact(event: Event,contact: LenderContact) {
  this.confirmationService.confirm({
    target: event.target as EventTarget,
    message: 'Are you sure you want to delete this contact?',
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptIcon: "none",
    rejectIcon: "none",
    acceptLabel: 'Delete',
    rejectLabel: 'Cancel',
    rejectButtonStyleClass: "p-button-text",
    accept: () => {
      this.onDeleteContact(contact);

    },
    reject: () => {
      // this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    }
  });
}

onDeleteContact(contact: LenderContact) {
  console.log(contact);
  this.lenderContactsService.deleteContact(contact.contactId);
}

}
